import React, { useState, useEffect } from 'react';
import { xeroApi, XeroConnectionStatus } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import './Xero.css';

interface SyncPreview {
  total_xero_contacts: number;
  new_customers: number;
  updated_customers: number;
  duplicates_found: number;
  preview_new: any[];
  preview_updates: any[];
  preview_duplicates: any[];
}

interface DuplicateGroup {
  name: string;
  count: number;
  customers: any[];
}

interface XeroItem {
  id: number;
  xero_item_id: string;
  code: string | null;
  name: string;
  description: string | null;
  unit_price: number | null;
  is_sold: boolean;
  is_active: boolean;
  linked_products_count: number;
}

function Xero() {
  const [connectionStatus, setConnectionStatus] = useState<XeroConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncPreview, setSyncPreview] = useState<SyncPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  const [activeTab, setActiveTab] = useState<'sync' | 'duplicates' | 'products' | 'skuMapping'>('sync');
  
  // Products/Items state
  const [items, setItems] = useState<XeroItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [syncingItems, setSyncingItems] = useState(false);
  const [unlinkedProducts, setUnlinkedProducts] = useState<any[]>([]);
  const [selectedItem, setSelectedItem] = useState<XeroItem | null>(null);
  const [itemProducts, setItemProducts] = useState<any[]>([]);
  const [linkSel, setLinkSel] = useState<Set<number>>(new Set());
  const [unlinkedFilter, setUnlinkedFilter] = useState<string>('');
  
  // Search and SKU Mapping state
  const [itemSearch, setItemSearch] = useState('');
  const [skuMappings, setSkuMappings] = useState<any[]>([]);
  const [modalItemSearch, setModalItemSearch] = useState('');
  const [loadingSkuMappings, setLoadingSkuMappings] = useState(false);
  const [skuSearch, setSkuSearch] = useState('');
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
  const [mappingItem, setMappingItem] = useState<{sku: string, name: string} | null>(null);

  useEffect(() => {
    loadConnectionStatus();
  }, []);

  const loadConnectionStatus = async () => {
    try {
      const status = await xeroApi.getStatus();
      setConnectionStatus(status);
      if (status.connected) {
        loadSyncPreview();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load connection status');
    } finally {
      setLoading(false);
    }
  };

  const loadSyncPreview = async () => {
    setLoadingPreview(true);
    try {
      const preview = await xeroApi.previewSync();
      setSyncPreview(preview);
    } catch (error: any) {
      console.error('Failed to load sync preview:', error);
    } finally {
      setLoadingPreview(false);
    }
  };

  const loadDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const result = await xeroApi.findDuplicates();
      setDuplicates(result.duplicates);
    } catch (error: any) {
      toast.error('Failed to load duplicates');
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const loadItems = async (search?: string) => {
    setLoadingItems(true);
    try {
      const data = await xeroApi.getItems(search || itemSearch);
      setItems(data);
    } catch (error: any) {
      toast.error('Failed to load items');
    } finally {
      setLoadingItems(false);
    }
  };

  const loadSkuMappings = async () => {
    setLoadingSkuMappings(true);
    try {
      const data = await xeroApi.getSkuMappings(skuSearch, showUnmappedOnly);
      setSkuMappings(data);
    } catch (error: any) {
      toast.error("Failed to load SKU mappings");
    } finally {
      setLoadingSkuMappings(false);
    }
  };

  const handleMapSku = async (sku: string, xeroItemId: number) => {
    try {
      const result = await xeroApi.mapSkuToXero(sku, xeroItemId);
      toast.success(result.message);
      setMappingItem(null); setModalItemSearch('');
      loadSkuMappings();
    } catch (error: any) {
      toast.error("Failed to map SKU");
    }
  };

  const handleUnmapSku = async (sku: string) => {
    try {
      const result = await xeroApi.unmapSku(sku);
      toast.success(result.message);
      loadSkuMappings();
    } catch (error: any) {
      toast.error("Failed to unmap SKU");
    }
  };

  const handleSyncItems = async () => {
    setSyncingItems(true);
    try {
      const result = await xeroApi.syncItems();
      toast.success(result.message);
      loadItems();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to sync items');
    } finally {
      setSyncingItems(false);
    }
  };

  const loadUnlinkedProducts = async () => {
    try {
      const result = await xeroApi.getUnlinkedProducts();
      setUnlinkedProducts(result.products);
    } catch (error: any) {
      console.error('Failed to load unlinked products:', error);
    }
  };

  const loadItemProducts = async (item: XeroItem) => {
    setSelectedItem(item);
    setLinkSel(new Set());
    setUnlinkedFilter('');
    try {
      const result = await xeroApi.getItemProducts(item.id);
      setItemProducts(result.products);
    } catch (error: any) {
      toast.error('Failed to load linked products');
    }
  };

  const handleLinkProducts = async (itemId: number, productIds: number[]) => {
    if (productIds.length === 0) return;
    try {
      const result = await xeroApi.linkItemToProducts(itemId, productIds);
      toast.success(result.message);
      setLinkSel(new Set());
      loadItems();
      loadUnlinkedProducts();
      if (selectedItem) {
        const refreshed = await xeroApi.getItemProducts(itemId);
        setItemProducts(refreshed.products);
      }
    } catch (error: any) {
      toast.error('Failed to link products');
    }
  };

  const handleUnlinkProduct = async (itemId: number, productId: number) => {
    try {
      await xeroApi.unlinkProduct(itemId, productId);
      toast.success('Product unlinked');
      loadItems();
      loadUnlinkedProducts();
      const refreshed = await xeroApi.getItemProducts(itemId);
      setItemProducts(refreshed.products);
    } catch (error: any) {
      toast.error('Failed to unlink product');
    }
  };

  const toggleLinkSel = (id: number) => {
    const n = new Set(linkSel);
    if (n.has(id)) n.delete(id); else n.add(id);
    setLinkSel(n);
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const authUrl = await xeroApi.connect();
      const width = 600;
      const height = 700;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      
      const popup = window.open(
        authUrl,
        'Xero Authentication',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );

      if (!popup) {
        toast.error('Popup blocked. Please allow popups for this site.');
        setConnecting(false);
        return;
      }

      const messageHandler = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'XERO_AUTH_RESULT') {
          window.removeEventListener('message', messageHandler);
          if (event.data.status === 'success') {
            toast.success('Connected to Xero!');
            loadConnectionStatus();
          } else {
            toast.error(event.data.message || 'Connection failed');
          }
          setConnecting(false);
        }
      };

      window.addEventListener('message', messageHandler);
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          setConnecting(false);
        }
      }, 500);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to connect');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    const password = window.prompt('Enter your password to disconnect Xero:');
    if (!password) return;

    try {
      await xeroApi.disconnect(password);
      toast.success('Xero disconnected');
      setConnectionStatus({ connected: false, tenant_id: null, connected_at: null });
      setSyncPreview(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await xeroApi.syncCustomers();
      toast.success(result.message);
      if (result.errors.length > 0) {
        console.error('Sync errors:', result.errors);
      }
      loadSyncPreview();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleMerge = async (keepId: number, mergeIds: number[], name: string) => {
    if (!window.confirm(`Merge ${mergeIds.length + 1} "${name}" records into one?`)) return;
    
    try {
      const result = await xeroApi.mergeCustomers(keepId, mergeIds);
      toast.success(result.message);
      loadDuplicates();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Merge failed');
    }
  };

  if (loading) {
    return <div className="xero-page"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="xero-page">
      <div className="page-header">
        <h1>Xero Integration</h1>
        <p className="page-description">Sync customers from Xero and manage duplicates</p>
      </div>

      {/* Connection Card */}
      <div className="card">
        <div className="connection-row">
          <div className="connection-status">
            <span className={`status-dot ${connectionStatus?.connected ? 'connected' : 'disconnected'}`}></span>
            <div>
              <strong>{connectionStatus?.connected ? 'Connected' : 'Not Connected'}</strong>
              {connectionStatus?.connected && connectionStatus.connected_at && (
                <p className="status-detail">
                  Since {format(new Date(connectionStatus.connected_at), 'MMM d, yyyy')}
                </p>
              )}
            </div>
          </div>
          <div className="connection-actions">
            {connectionStatus?.connected ? (
              <button onClick={handleDisconnect} className="btn btn-outline btn-danger">
                Disconnect
              </button>
            ) : (
              <button onClick={handleConnect} className="btn btn-primary" disabled={connecting}>
                {connecting ? 'Connecting...' : 'Connect to Xero'}
              </button>
            )}
          </div>
        </div>
      </div>

      {connectionStatus?.connected && (
        <>
          {/* Tabs */}
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'sync' ? 'active' : ''}`}
              onClick={() => setActiveTab('sync')}
            >
              Customer Sync
            </button>
            <button 
              className={`tab ${activeTab === 'duplicates' ? 'active' : ''}`}
              onClick={() => { setActiveTab('duplicates'); loadDuplicates(); }}
            >
              Duplicates
            </button>
            <button 
              className={`tab ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => { setActiveTab('products'); loadItems(); loadUnlinkedProducts(); }}
            >
              Products
            </button>
            <button 
              className={`tab ${activeTab === 'skuMapping' ? 'active' : ''}`}
              onClick={() => { setActiveTab('skuMapping'); loadSkuMappings(); }}
            >
              SKU Mapping
            </button>
          </div>

          {activeTab === 'sync' && (
            <div className="card">
              <div className="card-header">
                <h2>Customer Sync</h2>
                <div className="card-actions">
                  <button 
                    onClick={loadSyncPreview} 
                    className="btn btn-outline btn-sm"
                    disabled={loadingPreview}
                  >
                    {loadingPreview ? 'Loading...' : 'Refresh Preview'}
                  </button>
                  <button 
                    onClick={handleSync} 
                    className="btn btn-primary"
                    disabled={syncing || !syncPreview}
                  >
                    {syncing ? 'Syncing...' : 'Sync Now'}
                  </button>
                </div>
              </div>

              {syncPreview && (
                <div className="sync-preview">
                  <div className="sync-stats">
                    <div className="stat">
                      <div className="stat-value">{syncPreview.total_xero_contacts}</div>
                      <div className="stat-label">Xero Contacts</div>
                    </div>
                    <div className="stat new">
                      <div className="stat-value">{syncPreview.new_customers}</div>
                      <div className="stat-label">New</div>
                    </div>
                    <div className="stat update">
                      <div className="stat-value">{syncPreview.updated_customers}</div>
                      <div className="stat-label">To Update</div>
                    </div>
                    <div className="stat warning">
                      <div className="stat-value">{syncPreview.duplicates_found}</div>
                      <div className="stat-label">Duplicates</div>
                    </div>
                  </div>

                  {syncPreview.preview_new.length > 0 && (
                    <div className="preview-section">
                      <h3>New Customers (sample)</h3>
                      <div className="preview-list">
                        {syncPreview.preview_new.map((c, i) => (
                          <div key={i} className="preview-item new">
                            <span className="badge new">NEW</span>
                            <span>{c.name}</span>
                            {c.email && <span className="email">{c.email}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {syncPreview.preview_updates.length > 0 && (
                    <div className="preview-section">
                      <h3>Updates (sample)</h3>
                      <div className="preview-list">
                        {syncPreview.preview_updates.map((c, i) => (
                          <div key={i} className="preview-item update">
                            <span className="badge update">{c.action === 'link' ? 'LINK' : 'UPDATE'}</span>
                            <span>{c.old_name}</span>
                            {c.old_name !== c.new_name && (
                              <>
                                <span className="arrow">→</span>
                                <span>{c.new_name}</span>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="sync-info">
                <p><strong>What sync does:</strong></p>
                <ul>
                  <li>Creates new customers from Xero contacts</li>
                  <li>Updates names if changed in Xero</li>
                  <li>Links existing customers by name match (if no Xero ID)</li>
                  <li>Preserves all existing data and mappings</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'duplicates' && (
            <div className="card">
              <div className="card-header">
                <h2>Duplicate Customers</h2>
                <button 
                  onClick={loadDuplicates} 
                  className="btn btn-outline btn-sm"
                  disabled={loadingDuplicates}
                >
                  {loadingDuplicates ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {duplicates.length === 0 ? (
                <p className="no-duplicates">No duplicate customers found! 🎉</p>
              ) : (
                <div className="duplicates-list">
                  {duplicates.map((group, i) => (
                    <div key={i} className="duplicate-group">
                      <div className="duplicate-header">
                        <strong>"{group.name}"</strong>
                        <span className="count">{group.count} records</span>
                      </div>
                      <div className="duplicate-items">
                        {group.customers.map((c: any) => (
                          <div key={c.id} className="duplicate-item">
                            <div className="item-info">
                              <span className="item-name">{c.xero_name}</span>
                              {c.xero_customer_id && (
                                <span className="xero-linked">Xero linked</span>
                              )}
                              {c.contact_email && (
                                <span className="item-email">{c.contact_email}</span>
                              )}
                            </div>
                            <div className="item-id">ID: {c.id}</div>
                          </div>
                        ))}
                      </div>
                      <button 
                        className="btn btn-outline btn-sm"
                        onClick={() => {
                          const keep = group.customers.find((c: any) => c.xero_customer_id) || group.customers[0];
                          const merge = group.customers.filter((c: any) => c.id !== keep.id).map((c: any) => c.id);
                          handleMerge(keep.id, merge, group.name);
                        }}
                      >
                        Merge (keep Xero-linked)
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'products' && (
            <div className="card">
              <div className="card-header">
                <h2>Xero Products</h2>
                <div className="card-actions">
                  <button 
                    onClick={() => loadItems()} 
                    className="btn btn-outline btn-sm"
                    disabled={loadingItems}
                  >
                    {loadingItems ? 'Loading...' : 'Refresh'}
                  </button>
                  <button 
                    onClick={handleSyncItems} 
                    className="btn btn-primary"
                    disabled={syncingItems}
                  >
                    {syncingItems ? 'Syncing...' : 'Sync from Xero'}
                  </button>
                </div>
              </div>

              <p className="section-info">
                Import products from Xero and link them to services (M365, Cove, etc.) for invoice automation.
              </p>

              {items.length === 0 ? (
                <p className="no-items">No products found. Click "Sync from Xero" to import.</p>
              ) : (
                <div className="items-grid">
                  {items.map(item => (
                    <div key={item.id} className="item-card" onClick={() => loadItemProducts(item)}>
                      <div className="item-header">
                        <strong>{item.name}</strong>
                        {item.code && <span className="item-code">{item.code}</span>}
                      </div>
                      {item.description && (
                        <p className="item-desc">{item.description.substring(0, 100)}</p>
                      )}
                      <div className="item-footer">
                        {item.unit_price && (
                          <span className="item-price">R{item.unit_price.toFixed(2)}</span>
                        )}
                        <span className={`linked-badge ${item.linked_products_count > 0 ? 'has-links' : ''}`}>
                          {item.linked_products_count} linked
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Unlinked Products Section */}
              {unlinkedProducts.length > 0 && (
                <div className="unlinked-section">
                  <h3>Unlinked Service Products ({unlinkedProducts.length})</h3>
                  <p className="section-hint">Click any Xero product above to open it, then tick these and "Link selected" to price them for invoicing.</p>
                  <div className="unlinked-list">
                    {unlinkedProducts.slice(0, 20).map((p: any) => (
                      <div key={p.id} className="unlinked-item">
                        <div className="unlinked-info">
                          <span className="service-badge">{p.service_type}</span>
                          <span className="product-name">{p.product_name}</span>
                          <span className="customer-name">{p.customer_name}</span>
                        </div>
                        <span className="quantity">x{p.quantity}</span>
                      </div>
                    ))}
                    {unlinkedProducts.length > 20 && (
                      <p className="more-hint">...and {unlinkedProducts.length - 20} more</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'skuMapping' && (
            <div className="card">
              <div className="card-header">
                <h2>M365 SKU Mapping</h2>
                <div className="card-actions">
                  <button 
                    onClick={loadSkuMappings} 
                    className="btn btn-outline btn-sm"
                    disabled={loadingSkuMappings}
                  >
                    {loadingSkuMappings ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
              </div>

              <p className="section-info">
                Map Microsoft 365 license SKUs to Xero products for invoice automation.
              </p>

              <div className="search-row">
                <input
                  type="text"
                  placeholder="Search SKU or product name..."
                  value={skuSearch}
                  onChange={(e) => setSkuSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadSkuMappings()}
                  className="search-input"
                />
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={showUnmappedOnly}
                    onChange={(e) => setShowUnmappedOnly(e.target.checked)}
                  />
                  Unmapped only
                </label>
                <button onClick={loadSkuMappings} className="btn btn-outline btn-sm">Search</button>
              </div>

              {skuMappings.length === 0 ? (
                <p className="no-items">
                  {loadingSkuMappings ? 'Loading...' : 'No M365 SKUs found. Import licenses from Partner Center first.'}
                </p>
              ) : (
                <div className="sku-mapping-table">
                  <table>
                    <thead>
                      <tr>
                        <th>M365 Product</th>
                        <th>SKU</th>
                        <th>Qty</th>
                        <th>Customers</th>
                        <th>Xero Product</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {skuMappings.map((sku: any) => (
                        <tr key={sku.sku_part_number} className={sku.xero_item_id ? 'mapped' : 'unmapped'}>
                          <td className="product-name">{sku.product_name}</td>
                          <td className="sku-code">{sku.sku_part_number}</td>
                          <td className="qty">{sku.total_quantity}</td>
                          <td className="customers">{sku.customer_count}</td>
                          <td className="xero-product">
                            {sku.xero_item_name ? (
                              <span className="mapped-badge">
                                {sku.xero_item_code && <span className="code">{sku.xero_item_code}</span>}
                                {sku.xero_item_name}
                              </span>
                            ) : (
                              <span className="unmapped-badge">Not mapped</span>
                            )}
                          </td>
                          <td className="actions">
                            {sku.xero_item_id ? (
                              <button 
                                className="btn btn-outline btn-sm btn-danger"
                                onClick={() => handleUnmapSku(sku.sku_part_number)}
                              >
                                Unmap
                              </button>
                            ) : (
                              <button 
                                className="btn btn-primary btn-sm"
                                onClick={() => setMappingItem({ sku: sku.sku_part_number, name: sku.product_name })}
                              >
                                Map
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* SKU Mapping Modal */}
          {mappingItem && (
            <div className="modal-overlay" onClick={() => { setMappingItem(null); setModalItemSearch(''); }}>
              <div className="modal mapping-modal" onClick={e => e.stopPropagation()}>
                <h2>Map SKU to Xero Product</h2>
                <p className="mapping-sku">
                  <strong>M365:</strong> {mappingItem.name}<br/>
                  <small>{mappingItem.sku}</small>
                </p>
                
                <div className="xero-items-select">
                  <p><strong>Select Xero Product:</strong></p>
                  <input
                    type="text"
                    placeholder="Search Xero products..."
                    value={modalItemSearch}
                    onChange={(e) => setModalItemSearch(e.target.value)}
                    className="search-input"
                    style={{ marginBottom: "10px", width: "100%" }}
                  />
                  {items.length === 0 ? (
                    <p>No Xero products found. Sync products from Xero first.</p>
                  ) : (
                    <div className="items-list">
                      {items.filter(item => !modalItemSearch || item.name?.toLowerCase().includes(modalItemSearch.toLowerCase()) || item.code?.toLowerCase().includes(modalItemSearch.toLowerCase())).map(item => (
                        <div 
                          key={item.id} 
                          className="selectable-item"
                          onClick={() => handleMapSku(mappingItem.sku, item.id)}
                        >
                          <div className="item-info">
                            {item.code && <span className="item-code">{item.code}</span>}
                            <span className="item-name">{item.name}</span>
                          </div>
                          {item.unit_price && (
                            <span className="item-price">R{item.unit_price.toFixed(2)}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="modal-actions">
                  <button className="btn btn-outline" onClick={() => { setMappingItem(null); setModalItemSearch(''); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Item Detail / Linking Modal */}
          {selectedItem && (
            <div className="modal-overlay" onClick={() => setSelectedItem(null)}>
              <div className="modal modal-tall" onClick={e => e.stopPropagation()}>
                <h2>{selectedItem.name}</h2>
                <p className="modal-code">
                  {selectedItem.code && <>Code: {selectedItem.code} · </>}
                  {selectedItem.unit_price != null ? `Price: R${selectedItem.unit_price.toFixed(2)}` : 'No price set in Xero'}
                </p>

                <h3>Linked Products ({itemProducts.length})</h3>
                {itemProducts.length === 0 ? (
                  <p className="section-hint">No products linked to this Xero item yet.</p>
                ) : (
                  <div className="linked-products-list">
                    {itemProducts.map((p: any) => (
                      <div key={p.id} className="linked-product">
                        <span className="service-badge">{p.service_type}</span>
                        <span className="lp-name">{p.product_name}</span>
                        <span className="customer">{p.customer_name}</span>
                        <span className="qty">x{p.quantity}</span>
                        <button className="btn btn-outline btn-sm btn-danger" onClick={() => handleUnlinkProduct(selectedItem.id, p.id)}>Unlink</button>
                      </div>
                    ))}
                  </div>
                )}

                <h3>Link more products</h3>
                <div className="search-row" style={{ marginBottom: '8px' }}>
                  <select value={unlinkedFilter} onChange={e => setUnlinkedFilter(e.target.value)} className="search-input">
                    <option value="">All services</option>
                    <option value="cove">Cove</option>
                    <option value="proxmox">Proxmox</option>
                    <option value="whmcs">WHMCS</option>
                    <option value="microsoft365">Microsoft 365</option>
                    <option value="sentinelone">SentinelOne</option>
                    <option value="cloudblue">CloudBlue</option>
                  </select>
                </div>
                {(() => {
                  const avail = unlinkedProducts.filter((p: any) => !unlinkedFilter || p.service_type === unlinkedFilter);
                  if (avail.length === 0) {
                    return <p className="section-hint">No unlinked products{unlinkedFilter ? ` for ${unlinkedFilter}` : ''}.</p>;
                  }
                  return (
                    <>
                      <div className="link-products-list">
                        {avail.slice(0, 200).map((p: any) => (
                          <label key={p.id} className="link-product-row">
                            <input type="checkbox" checked={linkSel.has(p.id)} onChange={() => toggleLinkSel(p.id)} />
                            <span className="service-badge">{p.service_type}</span>
                            <span className="lp-name">{p.product_name}</span>
                            <span className="customer-name">{p.customer_name}</span>
                            <span className="qty">x{p.quantity}</span>
                          </label>
                        ))}
                        {avail.length > 200 && <p className="more-hint">…and {avail.length - 200} more (use the service filter to narrow).</p>}
                      </div>
                    </>
                  );
                })()}

                <div className="modal-actions">
                  <button
                    className="btn btn-primary"
                    disabled={linkSel.size === 0}
                    onClick={() => handleLinkProducts(selectedItem.id, Array.from(linkSel))}
                  >
                    Link {linkSel.size} selected
                  </button>
                  <button className="btn btn-outline" onClick={() => setSelectedItem(null)}>Close</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Xero;
